/**
 * Contacts / linked accounts for a client (portal permissions, invites).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import DrawIcon from '@mui/icons-material/Draw';
import PaymentIcon from '@mui/icons-material/Payment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import api, {
  getClientContacts,
  addClientContact,
  updateClientContact,
  removeClientContact,
  inviteContactPortal,
  searchContacts,
  createContactRecord,
} from '../api';

const RELATIONSHIP_OPTIONS = [
  'Primary',
  'Spouse',
  'Dependent',
  'CFO',
  'Bookkeeper',
  'Trustee',
  'Beneficiary',
  'Other',
];

const PORTAL_ACCESS_OPTIONS = [
  { value: 'full', label: 'Full Access' },
  { value: 'view_only', label: 'View Only' },
  { value: 'no_access', label: 'No Access' },
];

const AVATAR_PALETTE_KEYS = ['primary', 'secondary', 'info', 'success', 'warning'];

function paletteIndexForName(name) {
  const s = String(name || '');
  let n = 0;
  for (let i = 0; i < s.length; i += 1) n += s.charCodeAt(i);
  return n % AVATAR_PALETTE_KEYS.length;
}

function contactDisplayName(c) {
  if (!c) return '';
  const dn = (c.display_name || '').trim();
  if (dn) return dn;
  const a = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return a || `Contact #${c.id}`;
}

function initialsFromContact(c) {
  const name = contactDisplayName(c);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  return (name[0] || '?').toUpperCase();
}

function normalizeContactsResponse(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.contacts)) return data.contacts;
  return [];
}

export function ClientContactsSidebarMini({ clientId, scrollToId, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (clientId == null) return;
    setLoading(true);
    try {
      const res = await getClientContacts(clientId);
      setRows(normalizeContactsResponse(res.data));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const scroll = () => {
    const el = typeof document !== 'undefined' && scrollToId ? document.getElementById(scrollToId) : null;
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const preview = rows.slice(0, 3);
  const more = Math.max(0, rows.length - 3);

  if (clientId == null) return null;

  return (
    <PaperMiniCard title="Contacts" onClickTitleArea={scroll}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={22} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No contacts linked yet.
        </Typography>
      ) : (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          {preview.map((row) => {
            const c = row.contact;
            if (!c?.id) return null;
            const pk = paletteIndexForName(contactDisplayName(c));
            const hue = AVATAR_PALETTE_KEYS[pk];
            return (
              <Avatar
                key={row.id ?? `${c.id}-${row.relationship}`}
                title={contactDisplayName(c)}
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: '0.75rem',
                  bgcolor: `${hue}.main`,
                  color: `${hue}.contrastText`,
                }}
              >
                {initialsFromContact(c)}
              </Avatar>
            );
          })}
          {more > 0 ? (
            <Chip size="small" label={`+${more} more`} variant="outlined" onClick={scroll} sx={{ cursor: 'pointer' }} />
          ) : null}
        </Stack>
      )}
    </PaperMiniCard>
  );
}

function PaperMiniCard({ title, children, onClickTitleArea }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        boxShadow: 'none',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="subtitle1"
        component="button"
        type="button"
        onClick={onClickTitleArea}
        sx={{
          fontWeight: 600,
          mb: 1.5,
          color: 'text.primary',
          border: 'none',
          background: 'none',
          padding: 0,
          cursor: 'pointer',
          textAlign: 'left',
          width: '100%',
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function ClientContacts({
  clientId,
  onChanged,
  refreshKey = 0,
  openAddSignal = false,
  onOpenAddConsumed,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addTab, setAddTab] = useState(0);
  const [saving, setSaving] = useState(false);

  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState(null);

  const [existingForm, setExistingForm] = useState({
    relationship: 'Other',
    portal_access: 'no_access',
    can_sign_documents: false,
    can_pay_invoices: false,
    receive_notifications: true,
    is_primary: false,
  });

  const [newForm, setNewForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    relationship: 'Other',
    portal_access: 'view_only',
    can_sign_documents: false,
    can_pay_invoices: false,
    receive_notifications: true,
    is_primary: false,
  });

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({
    relationship: 'Other',
    portal_access: 'no_access',
    can_sign_documents: false,
    can_pay_invoices: false,
    receive_notifications: true,
    is_primary: false,
  });

  const [profileOpen, setProfileOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [inviteConfirmOpen, setInviteConfirmOpen] = useState(false);
  const [inviteRow, setInviteRow] = useState(null);
  const [inviteNoEmailOpen, setInviteNoEmailOpen] = useState(false);

  const notify = useCallback(() => {
    onChanged?.();
  }, [onChanged]);

  const load = useCallback(async () => {
    if (clientId == null) return;
    setLoading(true);
    setError('');
    try {
      const res = await getClientContacts(clientId);
      setRows(normalizeContactsResponse(res.data));
    } catch {
      setError('Could not load contacts.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const runSearch = useMemo(
    () =>
      debounceFn(async (q) => {
        const query = String(q || '').trim();
        if (query.length < 2) {
          setSearchOptions([]);
          return;
        }
        setSearchLoading(true);
        try {
          const res = await searchContacts(query);
          setSearchOptions(normalizeContactsResponse(res.data));
        } catch {
          setSearchOptions([]);
        } finally {
          setSearchLoading(false);
        }
      }, 300),
    []
  );

  const openAdd = useCallback(() => {
    setAddTab(0);
    setSelectedExisting(null);
    setExistingForm({
      relationship: 'Other',
      portal_access: 'no_access',
      can_sign_documents: false,
      can_pay_invoices: false,
      receive_notifications: true,
      is_primary: false,
    });
    setNewForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      mobile: '',
      relationship: 'Other',
      portal_access: 'view_only',
      can_sign_documents: false,
      can_pay_invoices: false,
      receive_notifications: true,
      is_primary: false,
    });
    setAddOpen(true);
  }, []);

  useEffect(() => {
    if (!openAddSignal) return;
    openAdd();
    onOpenAddConsumed?.();
  }, [openAddSignal, openAdd, onOpenAddConsumed]);

  const closeAdd = () => {
    if (!saving) setAddOpen(false);
  };

  const buildLinkPayload = (base) => {
    const isPrimary = base.relationship === 'Primary' || !!base.is_primary;
    return {
      relationship: base.relationship === 'Primary' ? 'Primary' : base.relationship,
      portal_access: base.portal_access,
      can_sign_documents: !!base.can_sign_documents,
      can_pay_invoices: !!base.can_pay_invoices,
      receive_notifications: !!base.receive_notifications,
      is_primary: isPrimary,
    };
  };

  const handleLinkExisting = async () => {
    if (!selectedExisting?.id) return;
    setSaving(true);
    setError('');
    try {
      await addClientContact(clientId, {
        contact_id: selectedExisting.id,
        ...buildLinkPayload(existingForm),
      });
      setAddOpen(false);
      await load();
      notify();
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.detail || 'Could not link contact.';
      setError(typeof msg === 'string' ? msg : 'Could not link contact.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndLink = async () => {
    const fn = (newForm.first_name || '').trim();
    const ln = (newForm.last_name || '').trim();
    if (!fn || !ln) {
      setError('First and last name are required.');
      return;
    }
    if (newForm.portal_access !== 'no_access' && !(newForm.email || '').trim()) {
      setError('Email is required for portal access.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const mobileJoined = [newForm.mobile, newForm.phone].map((x) => String(x || '').trim()).filter(Boolean)[0] || '';
      const createRes = await createContactRecord({
        first_name: fn,
        last_name: ln,
        email: (newForm.email || '').trim() || undefined,
        mobile: mobileJoined,
        display_name: `${fn} ${ln}`.trim(),
      });
      const newId = createRes.data?.id;
      if (!newId) {
        setError('Could not create contact.');
        return;
      }
      await addClientContact(clientId, {
        contact_id: newId,
        ...buildLinkPayload(newForm),
      });
      setAddOpen(false);
      await load();
      notify();
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.detail || 'Could not create contact.';
      setError(typeof msg === 'string' ? msg : 'Could not create contact.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editRow?.contact?.id) return;
    setSaving(true);
    setError('');
    try {
      await updateClientContact(clientId, editRow.contact.id, {
        relationship: editForm.relationship === 'Primary' ? 'Primary' : editForm.relationship,
        portal_access: editForm.portal_access,
        can_sign_documents: editForm.can_sign_documents,
        can_pay_invoices: editForm.can_pay_invoices,
        receive_notifications: editForm.receive_notifications,
        is_primary: editForm.relationship === 'Primary' || editForm.is_primary,
      });
      setEditOpen(false);
      setEditRow(null);
      await load();
      notify();
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.detail || 'Could not save.';
      setError(typeof msg === 'string' ? msg : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (row) => {
    if (!row?.contact?.id) return;
    setMenuAnchor(null);
    setMenuRow(null);
    setError('');
    try {
      await removeClientContact(clientId, row.contact.id);
      await load();
      notify();
    } catch {
      setError('Could not unlink contact.');
    }
  };

  const openProfile = async (row) => {
    if (!row?.contact?.id) return;
    setMenuAnchor(null);
    setMenuRow(null);
    setProfileOpen(true);
    setProfileLoading(true);
    setProfileData(null);
    try {
      const res = await api.get(`/api/contacts/${row.contact.id}/`);
      setProfileData(res.data);
    } catch {
      setProfileData(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const openInvite = (row) => {
    setMenuAnchor(null);
    setMenuRow(null);
    const email = row?.contact?.email;
    if (!email) setInviteNoEmailOpen(true);
    else {
      setInviteRow(row);
      setInviteConfirmOpen(true);
    }
  };

  const confirmInvite = async () => {
    if (!inviteRow?.contact?.id) return;
    setSaving(true);
    try {
      setInviteConfirmOpen(false);
      await inviteContactPortal(clientId, inviteRow.contact.id);
      setInviteRow(null);
      await load();
      notify();
    } catch {
      setError('Could not send invitation.');
    } finally {
      setSaving(false);
    }
  };

  const portalChip = (access) => {
    if (access === 'full') return <Chip size="small" label="Full Access" color="success" variant="outlined" />;
    if (access === 'view_only') return <Chip size="small" label="View Only" color="info" variant="outlined" />;
    return <Chip size="small" label="No Access" color="default" variant="outlined" />;
  };

  if (clientId == null) return null;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }} id="client-contacts-section">
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'text.primary' }}>
            Contacts
          </Typography>
          <Button variant="contained" size="small" startIcon={<PersonAddIcon />} onClick={openAdd}>
            Add Contact
          </Button>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 48, color: 'action.disabled', display: 'block', mx: 'auto', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No contacts linked
            </Typography>
            <Button variant="contained" size="small" startIcon={<PersonAddIcon />} onClick={openAdd}>
              Add first contact
            </Button>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {rows.map((row) => {
              const c = row.contact;
              if (!c) return null;
              const name = contactDisplayName(c);
              const pk = paletteIndexForName(name);
              const hue = AVATAR_PALETTE_KEYS[pk];
              const rel = row.relationship || '—';
              return (
                <Card key={row.id} variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: `${hue}.main`,
                          color: `${hue}.contrastText`,
                        }}
                      >
                        {initialsFromContact(c)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {name}
                          </Typography>
                          {row.is_primary ? (
                            <Tooltip title="Primary contact">
                              <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                                <StarIcon sx={{ fontSize: 20, color: 'warning.main' }} aria-hidden />
                              </Box>
                            </Tooltip>
                          ) : null}
                          <Chip size="small" label={rel} variant="outlined" sx={{ height: 22 }} />
                          {row.portal_invited_at ? (
                            <Chip
                              size="small"
                              label={`Invitation sent ${new Date(row.portal_invited_at).toLocaleString()}`}
                              color="success"
                              variant="outlined"
                              sx={{ height: 22 }}
                            />
                          ) : null}
                        </Stack>
                        {c.email ? (
                          <Link href={`mailto:${c.email}`} variant="body2" sx={{ display: 'block', mt: 0.5 }}>
                            {c.email}
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            No email
                          </Typography>
                        )}
                        {(c.phone || c.mobile) ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {[c.phone, c.mobile].filter(Boolean).join(' · ')}
                          </Typography>
                        ) : null}
                        <Box sx={{ mt: 1 }}>{portalChip(row.portal_access)}</Box>
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Tooltip title="Can sign documents">
                          <Box component="span" sx={{ display: 'inline-flex', opacity: row.can_sign_documents ? 1 : 0.4 }}>
                            <DrawIcon color={row.can_sign_documents ? 'primary' : 'disabled'} fontSize="small" />
                          </Box>
                        </Tooltip>
                        <Tooltip title="Can pay invoices">
                          <Box component="span" sx={{ display: 'inline-flex', opacity: row.can_pay_invoices ? 1 : 0.4 }}>
                            <PaymentIcon color={row.can_pay_invoices ? 'primary' : 'disabled'} fontSize="small" />
                          </Box>
                        </Tooltip>
                        <Tooltip title="Receives notifications">
                          <Box component="span" sx={{ display: 'inline-flex', opacity: row.receive_notifications ? 1 : 0.4 }}>
                            <NotificationsIcon color={row.receive_notifications ? 'primary' : 'disabled'} fontSize="small" />
                          </Box>
                        </Tooltip>
                        <IconButton
                          size="small"
                          aria-label="More actions"
                          onClick={(e) => {
                            setMenuAnchor(e.currentTarget);
                            setMenuRow(row);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </CardContent>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => { setMenuAnchor(null); setMenuRow(null); }}>
        <MenuItem
          onClick={() => {
            if (!menuRow) return;
            setEditRow(menuRow);
            setEditForm({
              relationship: menuRow.relationship || 'Other',
              portal_access: menuRow.portal_access || 'no_access',
              can_sign_documents: !!menuRow.can_sign_documents,
              can_pay_invoices: !!menuRow.can_pay_invoices,
              receive_notifications: !!menuRow.receive_notifications,
              is_primary: !!menuRow.is_primary,
            });
            setEditOpen(true);
            setMenuAnchor(null);
            setMenuRow(null);
          }}
        >
          Edit Permissions
        </MenuItem>
        <MenuItem onClick={() => menuRow && openInvite(menuRow)}>Invite to Portal</MenuItem>
        <MenuItem onClick={() => menuRow && handleUnlink(menuRow)}>Unlink</MenuItem>
        <MenuItem onClick={() => menuRow && openProfile(menuRow)}>View Profile</MenuItem>
      </Menu>

      <Dialog open={addOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
        <DialogTitle>Add contact</DialogTitle>
        <DialogContent dividers>
          <Tabs value={addTab} onChange={(_, v) => setAddTab(v)} sx={{ mb: 2 }}>
            <Tab label="Existing Contact" />
            <Tab label="New Contact" />
          </Tabs>
          {addTab === 0 ? (
            <Stack spacing={2}>
              <Autocomplete
                loading={searchLoading}
                options={searchOptions}
                filterOptions={(opts) => opts}
                value={selectedExisting}
                onChange={(_, v) => setSelectedExisting(v)}
                onInputChange={(_, v) => runSearch(v)}
                getOptionLabel={(o) => (o ? contactDisplayName(o) : '')}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                renderInput={(params) => <TextField {...params} label="Search contacts" placeholder="Name or email" />}
              />
              {selectedExisting ? (
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {contactDisplayName(selectedExisting)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {selectedExisting.email || 'No email'}
                  </Typography>
                </Box>
              ) : null}
              <RelationPortalFields form={existingForm} setForm={setExistingForm} />
              <Button variant="contained" disabled={!selectedExisting || saving} onClick={handleLinkExisting}>
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Link Contact'}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  required
                  label="First name"
                  value={newForm.first_name}
                  onChange={(e) => setNewForm((f) => ({ ...f, first_name: e.target.value }))}
                  fullWidth
                  size="small"
                />
                <TextField
                  required
                  label="Last name"
                  value={newForm.last_name}
                  onChange={(e) => setNewForm((f) => ({ ...f, last_name: e.target.value }))}
                  fullWidth
                  size="small"
                />
              </Stack>
              <TextField
                label="Email"
                type="email"
                value={newForm.email}
                onChange={(e) => setNewForm((f) => ({ ...f, email: e.target.value }))}
                fullWidth
                size="small"
                helperText={newForm.portal_access !== 'no_access' ? 'Required for portal access' : ' '}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Phone" value={newForm.phone} onChange={(e) => setNewForm((f) => ({ ...f, phone: e.target.value }))} fullWidth size="small" />
                <TextField label="Mobile" value={newForm.mobile} onChange={(e) => setNewForm((f) => ({ ...f, mobile: e.target.value }))} fullWidth size="small" />
              </Stack>
              <RelationPortalFields form={newForm} setForm={setNewForm} />
              <Button variant="contained" disabled={saving} onClick={handleCreateAndLink}>
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Create & Link'}
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd} disabled={saving}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit permissions{editRow ? ` — ${contactDisplayName(editRow.contact)}` : ''}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="ec-rel">Relationship</InputLabel>
              <Select
                labelId="ec-rel"
                label="Relationship"
                value={RELATIONSHIP_OPTIONS.includes(editForm.relationship) ? editForm.relationship : 'Other'}
                onChange={(e) => setEditForm((f) => ({ ...f, relationship: e.target.value }))}
              >
                {RELATIONSHIP_OPTIONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="ec-pa">Portal Access</InputLabel>
              <Select
                labelId="ec-pa"
                label="Portal Access"
                value={editForm.portal_access}
                onChange={(e) => setEditForm((f) => ({ ...f, portal_access: e.target.value }))}
              >
                {PORTAL_ACCESS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.can_sign_documents}
                  onChange={(e) => setEditForm((f) => ({ ...f, can_sign_documents: e.target.checked }))}
                />
              }
              label="Can sign documents"
            />
            <FormControlLabel
              control={
                <Checkbox checked={editForm.can_pay_invoices} onChange={(e) => setEditForm((f) => ({ ...f, can_pay_invoices: e.target.checked }))} />
              }
              label="Can pay invoices"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.receive_notifications}
                  onChange={(e) => setEditForm((f) => ({ ...f, receive_notifications: e.target.checked }))}
                />
              }
              label="Receive notifications"
            />
            <FormControlLabel
              control={<Switch checked={editForm.is_primary} onChange={(e) => setEditForm((f) => ({ ...f, is_primary: e.target.checked }))} />}
              label="Primary contact"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={profileOpen} onClose={() => setProfileOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Contact profile</DialogTitle>
        <DialogContent dividers>
          {profileLoading ? (
            <CircularProgress />
          ) : profileData ? (
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {contactDisplayName(profileData)}
              </Typography>
              <Typography variant="body2">Email: {profileData.email || '—'}</Typography>
              <Typography variant="body2">Phone: {profileData.phone || '—'}</Typography>
              <Typography variant="body2">Mobile: {profileData.mobile || '—'}</Typography>
            </Stack>
          ) : (
            <Typography color="text.secondary">Profile unavailable.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inviteConfirmOpen} onClose={() => setInviteConfirmOpen(false)}>
        <DialogTitle>Send portal invitation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Send portal invitation to {inviteRow?.contact?.email ? (
              <strong>{inviteRow.contact.email}</strong>
            ) : (
              'this contact'
            )}
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmInvite} disabled={saving}>
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inviteNoEmailOpen} onClose={() => setInviteNoEmailOpen(false)}>
        <DialogTitle>Cannot invite</DialogTitle>
        <DialogContent>
          <Alert severity="warning">Contact needs an email to access portal.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteNoEmailOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function RelationPortalFields({ form, setForm }) {
  return (
    <>
      <FormControl fullWidth size="small">
        <InputLabel id="rel-l">Relationship</InputLabel>
        <Select
          labelId="rel-l"
          label="Relationship"
          value={RELATIONSHIP_OPTIONS.includes(form.relationship) ? form.relationship : 'Other'}
          onChange={(e) => setForm((f) => ({ ...f, relationship: e.target.value }))}
        >
          {RELATIONSHIP_OPTIONS.map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth size="small">
        <InputLabel id="pa-l">Portal Access</InputLabel>
        <Select
          labelId="pa-l"
          label="Portal Access"
          value={form.portal_access}
          onChange={(e) => setForm((f) => ({ ...f, portal_access: e.target.value }))}
        >
          {PORTAL_ACCESS_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox checked={form.can_sign_documents} onChange={(e) => setForm((f) => ({ ...f, can_sign_documents: e.target.checked }))} />
        }
        label="Can sign documents"
      />
      <FormControlLabel
        control={<Checkbox checked={form.can_pay_invoices} onChange={(e) => setForm((f) => ({ ...f, can_pay_invoices: e.target.checked }))} />}
        label="Can pay invoices"
      />
      <FormControlLabel
        control={
          <Checkbox checked={form.receive_notifications} onChange={(e) => setForm((f) => ({ ...f, receive_notifications: e.target.checked }))} />
        }
        label="Receive notifications"
      />
      <FormControlLabel
        control={<Switch checked={form.is_primary} onChange={(e) => setForm((f) => ({ ...f, is_primary: e.target.checked }))} />}
        label="Primary contact"
      />
    </>
  );
}

function debounceFn(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => {
      fn(...args);
    }, ms);
  };
}
